import React from 'react';
import {
  Accordion,
  Button,
  ButtonSize,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  CmhcFlexIcon
} from '@cmhc-schl/hearth-ui';
import { Box, Stack, Typography } from '@mui/material';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import FormatAmountWithDollar from '../../../../../HbtPortfolioManagement/utils/FormatAmountWithDollar';
import ErrorMessage, { SpinnerElement } from '../../../shared/ErrorMessage';
import { HbtMasterPoolPremiumsAndTaxesSectionProps } from '../../types';
import styles from './styles.module.scss';

const HbtMasterPoolPremiumsAndTaxesSection: React.FC<HbtMasterPoolPremiumsAndTaxesSectionProps> = ({
  data,
  toggleDrawer,
  shouldShowPremiumsAndTaxes,
  noDataAvailable,
  isError,
  isLoading
}) => {
  const { t } = useTranslation('HbtMasterPool');
  const { language } = i18n;
  const {
    premiumAmountDue,
    premiumAmountReceived,
    taxAmountDue,
    taxAmountReceived,
    totalAmountDue,
    unbalanced,
    dateOfLastPayment,
    totalAmountReceived
  } = data;
  return (
    <Accordion
      suppressBottomBorder
      accordionData={{
        heading: t('PI-Master-Pool-Premiums-Taxes-Title'),
        defaultExpanded: true,
        contentSlot: (
          <Box className={styles.premiumsAndTaxesSection}>
            {isLoading && !noDataAvailable && !shouldShowPremiumsAndTaxes && <SpinnerElement />}
            {noDataAvailable && !shouldShowPremiumsAndTaxes && (
              <ErrorMessage
                errorType={'no_data_error'}
                errorHeader={t('PI-Master-Pool-Premiums-Taxes-No-Data-Error-Header')}
                errorContent={t('PI-Master-Pool-Premiums-Taxes-No-Data-Error-Content')}
              />
            )}
            {isError && !noDataAvailable && !shouldShowPremiumsAndTaxes && (
              <ErrorMessage
                errorType={'api_error'}
                errorHeader={t('PI-Master-Pool-Premiums-Taxes-API-Error-Header')}
                errorContent={t('PI-Master-Pool-Premiums-Taxes-API-Error-Content')}
                serviceNowRequired
              />
            )}
            {shouldShowPremiumsAndTaxes && !isError && !noDataAvailable && !isLoading && (
              <Box className={styles.premiumsAndTaxesSectionStackWithIcon}>
                <Box>
                  <CmhcFlexIcon
                    className={styles.moneyCalculateIcon}
                    type="icon_money_calculate"
                    fill="#fff"
                    width={48}
                    height={48}
                  />
                </Box>
                <Stack className={styles.premiumsAndTaxesSectionStack}>
                  <Box className={styles.premiumsAndTaxTotalDue}>
                    <Typography>
                      <span>{t('PI-Master-Pool-Premiums-Taxes-Section-Total-Due')}</span>
                      <span className={styles.premiumsAndTaxTotalDueWithIcon}>
                        {FormatAmountWithDollar(+totalAmountDue, language, true)}{' '}
                        <span id={styles.premiumsAndTaxTotalDueWithIconSpecialSpan}>
                          / {FormatAmountWithDollar(+totalAmountReceived, language, true)}
                        </span>
                        {unbalanced && (
                          <span className={styles.premiumsAndTaxUnbalanced}>
                            <CmhcIcon size={CmhcIconSize.SMALL} type="icon_unbalanced" />
                            <Typography>
                              {t('PI-Master-Pool-Premiums-Taxes-Section-Unbalanced')}
                            </Typography>
                          </span>
                        )}
                      </span>
                    </Typography>
                  </Box>
                  <Box className={styles.premiumsAndTaxBreakdown}>
                    <Box>
                      <Typography>
                        <span>
                          {t('PI-Master-Pool-Premiums-Taxes-Section-Total-Premiums-Amount-Due')}
                        </span>
                        <span>{FormatAmountWithDollar(+premiumAmountDue, language, true)}</span>
                      </Typography>
                      <Typography>
                        <span>
                          {t('PI-Master-Pool-Premiums-Taxes-Section-Total-Premiums-Amount-Paid')}
                        </span>
                        <span>
                          {FormatAmountWithDollar(+premiumAmountReceived, language, true)}
                        </span>
                      </Typography>
                      <Typography>
                        <span>
                          {t('PI-Master-Pool-Premiums-Taxes-Section-Total-Taxes-Amount-Due')}
                        </span>
                        <span>{FormatAmountWithDollar(+taxAmountDue, language, true)}</span>
                      </Typography>
                      <Typography>
                        <span>
                          {t('PI-Master-Pool-Premiums-Taxes-Section-Total-Taxes-Amount-Paid')}
                        </span>
                        <span>{FormatAmountWithDollar(+taxAmountReceived, language, true)}</span>
                      </Typography>
                      <Typography>
                        <span>
                          {t('PI-Master-Pool-Premiums-Taxes-Section-Date-Of-Last-Payment')}
                        </span>
                        <span className={styles.dateOfLastPayment}>
                          {dateOfLastPayment ?? (
                            <CmhcIcon size={CmhcIconSize.MEDIUM} type="icon_indeterminate" />
                          )}
                        </span>
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    className={styles.premiumTaxBreakdownButton}
                    name="premium-tax-breakdown"
                    type="button"
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.TERTIARY}
                    leadingIcon={() => (
                      <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_question" />
                    )}
                    text={{
                      value: t('PI-Master-Pool-Premiums-Taxes-Section-Premium-And-Taxes-Breakdown')
                    }}
                    ariaText={{
                      value: t('PI-Master-Pool-Premiums-Taxes-Section-Premium-And-Taxes-Breakdown')
                    }}
                    onClick={() => toggleDrawer(true)}
                  />
                </Stack>
              </Box>
            )}
          </Box>
        )
      }}
    ></Accordion>
  );
};

export default HbtMasterPoolPremiumsAndTaxesSection;
